/**
 * /login — email + password.
 *
 * On success, restores the `?next=` path if present (e.g. user landed
 * here via RequireAuth) or falls back to /. If the server signals
 * must_change_password we route to /change-password instead.
 *
 * Someone who is ALREADY signed in is sent straight on rather than being
 * shown the form. Without that, anything which lands an authenticated
 * person here — a bookmark, a stale `?next=` link, or a momentary blip
 * in the route guard — traps them: the form has no idea they are signed
 * in, so they enter correct credentials, get bounced back, and try
 * again. This is the backstop that makes such a bounce a non-event
 * instead of a dead end.
 */

import { Alert, Box, Stack } from '@mui/material';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth';
import { uiType } from '../../theme';
import { AuthLayout } from './AuthLayout';
import { AuthPrimaryButton } from './AuthPrimaryButton';
import { AuthTextField } from './AuthTextField';
import { describeError } from './errorMessages';

export function LoginPage() {
  const { login, user, isReady } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // A `next` pointing back at an auth page would bounce forever now that
  // an authenticated visitor is redirected away from here — the exact
  // failure this redirect exists to prevent.
  /**
   * Why they are looking at this screen.
   *
   * The idle clock runs whether or not this tab has focus, so someone
   * working in another tab can be signed out without ever seeing the
   * warning. That is the right security behaviour and the wrong thing to
   * leave unexplained — an unannounced login form reads as the platform
   * losing their session, not protecting it.
   */
  const reason = params.get('reason');

  const requestedNext = params.get('next') || '/';
  const nextPath = /^\/(login|auth\/login|signup|auth\/signup)\b/.test(requestedNext)
    ? '/'
    : requestedNext;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const token = await login({ email, password });
      if (token.must_change_password) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // After every hook, so the hook order never changes between renders.
  if (isReady && user) {
    return <Navigate to={user.must_change_password ? '/change-password' : nextPath} replace />;
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Access your Hooten Young dashboard."
      footer={
        <>
          New here? <Link to="/signup">Request an account</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && reason === 'idle' && (
            <Alert severity="info" sx={{ fontSize: uiType.bodySm }}>
              You were signed out after 20 minutes without activity. Sign in to pick up where you
              left off.
            </Alert>
          )}

          <AuthTextField
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />

          <AuthTextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <AuthPrimaryButton type="submit" disabled={!email || !password} loading={submitting}>
            Sign in
          </AuthPrimaryButton>

          <Box sx={{ textAlign: 'center', pt: 0.5 }}>
            <Link
              to="/forgot-password"
              style={{
                fontSize: 13,
                color: '#bb8c3f',
                fontWeight: 600,
                textDecoration: 'none',
                letterSpacing: '0.04em',
              }}
            >
              Forgot your password?
            </Link>
          </Box>
        </Stack>
      </form>
    </AuthLayout>
  );
}
